import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Wallet, ClipboardList, Settings, Plus, Trash2, Check, UserCircle2, Wand2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}${p.startsWith("/api") ? "" : "/api/files/"}${p}` : "");
const TABS = [["creative", "Creative Studio", Sparkles], ["erp", "ERP", Wallet], ["production", "Production", ClipboardList], ["settings", "Settings", Settings]];
const TEXT_KINDS = [["logline", "Loglines"], ["treatment", "Treatment"], ["script", "Script scene"], ["idea", "Content ideas"], ["brief", "Creative brief"], ["proposal", "Proposal"]];
const IMG_KINDS = [["poster", "Poster"], ["concept", "Concept art"], ["storyboard", "Storyboard"]];

function Creative() {
  const [tkind, setTkind] = useState("logline");
  const [tctx, setTctx] = useState("");
  const [tout, setTout] = useState("");
  const [tloading, setTloading] = useState(false);
  const [ikind, setIkind] = useState("poster");
  const [iprompt, setIprompt] = useState("");
  const [iloading, setIloading] = useState(false);
  const [images, setImages] = useState([]);

  const loadImages = useCallback(async () => { try { const r = await api.genImages(); setImages(r.images || []); } catch { /* ignore */ } }, []);
  useEffect(() => { loadImages(); }, [loadImages]);

  const genText = async () => {
    if (!tctx.trim()) return toast.error("Describe your premise first");
    setTloading(true); setTout("");
    try { const r = await api.ai(tkind, tctx.trim()); setTout(r.output || ""); }
    catch { toast.error("Generation failed"); } finally { setTloading(false); }
  };
  const genImage = async () => {
    if (!iprompt.trim()) return toast.error("Enter an image prompt");
    setIloading(true);
    try { await api.generateImage(iprompt.trim(), ikind); toast.success("Image generated"); setIprompt(""); await loadImages(); }
    catch (e) { toast.error(e.response?.data?.detail || "Image generation failed"); } finally { setIloading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="creative-studio">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3">
        <div className="mono text-kinetic flex items-center gap-2"><Wand2 className="w-4 h-4" /> WRITE WITH AI</div>
        <Select value={tkind} onValueChange={setTkind}>
          <SelectTrigger data-testid="text-kind" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{TEXT_KINDS.map(([k, l]) => <SelectItem key={k} value={k} className="mono">{l.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="text-context" value={tctx} onChange={e => setTctx(e.target.value)} placeholder="Describe the premise, genre, tone, characters…" className="bg-black border-white/15 rounded-sm min-h-28" />
        <Button data-testid="text-generate" onClick={genText} disabled={tloading} className="kinetic-btn rounded-sm w-full">{tloading ? "GENERATING…" : "Generate"}</Button>
        {tout && <div data-testid="text-output" className="border border-white/10 rounded-sm bg-black p-4 text-white/85 text-sm whitespace-pre-wrap max-h-80 overflow-y-auto">{tout}</div>}
      </div>

      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3">
        <div className="mono text-kinetic flex items-center gap-2"><ImageIcon className="w-4 h-4" /> GENERATE VISUALS · NANO BANANA</div>
        <Select value={ikind} onValueChange={setIkind}>
          <SelectTrigger data-testid="img-kind" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{IMG_KINDS.map(([k, l]) => <SelectItem key={k} value={k} className="mono">{l.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="img-prompt" value={iprompt} onChange={e => setIprompt(e.target.value)} placeholder="e.g. A lone detective under neon rain, moody teal & magenta, film noir…" className="bg-black border-white/15 rounded-sm min-h-20" />
        <Button data-testid="img-generate" onClick={genImage} disabled={iloading} className="kinetic-btn rounded-sm w-full">{iloading ? "PAINTING…" : "Generate image"}</Button>
        <div className="grid grid-cols-2 gap-3" data-testid="img-gallery">
          {images.length === 0 && <div className="mono text-white/30 col-span-2 text-center py-6">NO IMAGES YET</div>}
          {images.map(im => (
            <a key={im.id} href={fileUrl(im.url)} target="_blank" rel="noreferrer" className="block border border-white/10 rounded-sm overflow-hidden" data-testid={`gen-img-${im.id}`}>
              <img src={fileUrl(im.url)} alt={im.prompt} className="w-full aspect-square object-cover" />
              <div className="mono text-white/40 p-2 truncate">{im.kind?.toUpperCase()} · {im.prompt}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function ERP() {
  const [budgets, setBudgets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bTitle, setBTitle] = useState("");
  const [bItems, setBItems] = useState([{ label: "", amount: "" }]);
  const [inv, setInv] = useState({ client: "", amount: "", due_date: "", notes: "" });

  const load = useCallback(async () => { setBudgets((await api.budgets()).budgets || []); setInvoices((await api.invoices()).invoices || []); }, []);
  useEffect(() => { load(); }, [load]);

  const addItem = () => setBItems(a => [...a, { label: "", amount: "" }]);
  const setItem = (i, k, v) => setBItems(a => a.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const saveBudget = async () => {
    if (!bTitle.trim()) return toast.error("Budget title required");
    const items = bItems.filter(i => i.label.trim()).map(i => ({ label: i.label, amount: Number(i.amount) || 0 }));
    await api.createBudget({ title: bTitle, items }); setBTitle(""); setBItems([{ label: "", amount: "" }]); await load(); toast.success("Budget saved");
  };
  const saveInvoice = async () => {
    if (!inv.client.trim()) return toast.error("Client required");
    await api.createInvoice({ ...inv, amount: Number(inv.amount) || 0 }); setInv({ client: "", amount: "", due_date: "", notes: "" }); await load(); toast.success("Invoice created");
  };
  const cycleStatus = async (i) => {
    const next = i.status === "draft" ? "sent" : i.status === "sent" ? "paid" : "draft";
    await api.updateInvoice(i.id, { status: next }); await load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="erp">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3">
        <div className="mono text-kinetic">BUDGETS</div>
        <Input data-testid="budget-title" value={bTitle} onChange={e => setBTitle(e.target.value)} placeholder="Budget title (e.g. Short Film Q3)" className="bg-black border-white/15 rounded-sm" />
        {bItems.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`budget-item-label-${i}`} value={it.label} onChange={e => setItem(i, "label", e.target.value)} placeholder="Line item" className="bg-black border-white/15 rounded-sm" />
            <Input data-testid={`budget-item-amount-${i}`} type="number" value={it.amount} onChange={e => setItem(i, "amount", e.target.value)} placeholder="₹" className="bg-black border-white/15 rounded-sm w-28" />
          </div>
        ))}
        <div className="flex gap-2">
          <Button data-testid="budget-add-item" onClick={addItem} variant="outline" size="sm" className="rounded-sm border-white/20 bg-transparent text-white/80 hover:bg-white/10"><Plus className="w-3.5 h-3.5 mr-1" />Line</Button>
          <Button data-testid="budget-save" onClick={saveBudget} className="kinetic-btn rounded-sm flex-1">Save budget</Button>
        </div>
        <div className="space-y-2 pt-2">
          {budgets.map(b => (
            <div key={b.id} className="border border-white/10 rounded-sm p-3" data-testid={`budget-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-sans font-bold">{b.title}</div>
                <div className="flex items-center gap-2"><span className="mono text-kinetic">₹{Number(b.total).toLocaleString("en-IN")}</span><Trash2 data-testid={`budget-del-${b.id}`} onClick={() => api.deleteBudget(b.id).then(load)} className="w-4 h-4 text-white/25 hover:text-coral" /></div>
              </div>
              <div className="mono text-white/40 mt-1">{b.items?.length || 0} LINE ITEMS</div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-5 space-y-3">
        <div className="mono text-kinetic">INVOICES</div>
        <Input data-testid="invoice-client" value={inv.client} onChange={e => setInv(s => ({ ...s, client: e.target.value }))} placeholder="Client / payer" className="bg-black border-white/15 rounded-sm" />
        <div className="flex gap-2">
          <Input data-testid="invoice-amount" type="number" value={inv.amount} onChange={e => setInv(s => ({ ...s, amount: e.target.value }))} placeholder="Amount ₹" className="bg-black border-white/15 rounded-sm" />
          <Input data-testid="invoice-due" value={inv.due_date} onChange={e => setInv(s => ({ ...s, due_date: e.target.value }))} placeholder="Due date" className="bg-black border-white/15 rounded-sm" />
        </div>
        <Button data-testid="invoice-save" onClick={saveInvoice} className="kinetic-btn rounded-sm w-full">Create invoice</Button>
        <div className="space-y-2 pt-2">
          {invoices.map(i => (
            <div key={i.id} className="border border-white/10 rounded-sm p-3 flex items-center justify-between" data-testid={`invoice-${i.id}`}>
              <div><div className="font-sans font-bold">{i.client}</div><div className="mono text-white/40 mt-0.5">₹{Number(i.amount).toLocaleString("en-IN")} {i.due_date ? `· due ${i.due_date}` : ""}</div></div>
              <div className="flex items-center gap-2">
                <button data-testid={`invoice-status-${i.id}`} onClick={() => cycleStatus(i)}
                  className={`mono px-2 py-1 rounded-sm border ${i.status === "paid" ? "border-kinetic text-kinetic bg-kinetic/10" : i.status === "sent" ? "border-white/30 text-white/80" : "border-white/15 text-white/50"}`}>{i.status.toUpperCase()}</button>
                <Trash2 data-testid={`invoice-del-${i.id}`} onClick={() => api.deleteInvoice(i.id).then(load)} className="w-4 h-4 text-white/25 hover:text-coral" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Production() {
  const [tasks, setTasks] = useState([]);
  const [inp, setInp] = useState({ pre: "", pro: "", post: "" });
  const load = useCallback(async () => setTasks((await api.prodTasks()).tasks || []), []);
  useEffect(() => { load(); }, [load]);
  const add = async (phase) => { if (!inp[phase].trim()) return; await api.createProdTask({ phase, title: inp[phase].trim() }); setInp(s => ({ ...s, [phase]: "" })); await load(); };
  const PHASES = [["pre", "PRE-PRODUCTION"], ["pro", "PRODUCTION"], ["post", "POST-PRODUCTION"]];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="production-board">
      {PHASES.map(([ph, label]) => (
        <div key={ph} className="border border-white/10 bg-[#0C0C0C] rounded-lg p-4" data-testid={`phase-${ph}`}>
          <div className="mono text-kinetic mb-3">{label}</div>
          <div className="flex gap-2 mb-3">
            <Input data-testid={`task-input-${ph}`} value={inp[ph]} onChange={e => setInp(s => ({ ...s, [ph]: e.target.value }))} onKeyDown={e => e.key === "Enter" && add(ph)} placeholder="Add task…" className="bg-black border-white/15 rounded-sm h-9" />
            <Button data-testid={`task-add-${ph}`} onClick={() => add(ph)} className="kinetic-btn rounded-sm h-9 px-3"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {tasks.filter(t => t.phase === ph).map(t => (
              <div key={t.id} className="flex items-center gap-2 border border-white/10 rounded-sm px-3 py-2" data-testid={`task-${t.id}`}>
                <button data-testid={`task-toggle-${t.id}`} onClick={() => api.toggleProdTask(t.id).then(load)}
                  className={`w-4 h-4 rounded-sm border flex items-center justify-center ${t.done ? "bg-kinetic border-kinetic" : "border-white/30"}`}>{t.done && <Check className="w-3 h-3 text-black" />}</button>
                <span className={`flex-1 text-sm ${t.done ? "line-through text-white/40" : "text-white/85"}`}>{t.title}</span>
                <Trash2 data-testid={`task-del-${t.id}`} onClick={() => api.deleteProdTask(t.id).then(load)} className="w-3.5 h-3.5 text-white/25 hover:text-coral" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const { user } = useAuth();
  const [sub, setSub] = useState("account");
  const [s, setS] = useState(null);
  const [trash, setTrash] = useState([]);
  useEffect(() => { api.toolsSettings().then(setS); api.toolsTrash().then(r => setTrash(r.items || [])); }, []);
  const save = async (patch) => { const next = { ...s, ...patch }; setS(next); try { await api.updateToolsSettings(patch); } catch { /* ignore */ } };
  const setWidget = (k, v) => save({ widgets: { ...(s.widgets || {}), [k]: v } });
  const SUBS = [["account", "Account preferences"], ["general", "General"], ["widgets", "Widgets"], ["library", "Content Library"], ["trash", "Trash management"]];
  if (!s) return <div className="mono text-white/40">LOADING…</div>;
  return (
    <div className="space-y-5" data-testid="settings-tab">
      <div className="flex items-center gap-3 border border-white/10 bg-[#0C0C0C] rounded-lg p-4">
        <UserCircle2 className="w-9 h-9 text-white/40" />
        <div><div className="font-sans font-bold">{user?.name}</div><div className="mono text-white/40">{user?.email} · PLAN: {(user?.plan || "founders").toUpperCase()}</div></div>
      </div>
      <div className="flex gap-1 flex-wrap">
        {SUBS.map(([k, l]) => (
          <button key={k} data-testid={`settings-sub-${k}`} onClick={() => setSub(k)}
            className={`mono px-3 h-8 rounded-sm border ${sub === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>{l.toUpperCase()}</button>
        ))}
      </div>
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 max-w-xl">
        {sub === "account" && (
          <div className="space-y-4" data-testid="settings-account">
            <div><Label className="mono text-white/60">Language</Label>
              <Select value={s.language || "English"} onValueChange={v => save({ language: v })}>
                <SelectTrigger data-testid="pref-language" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{["English", "Hindi", "Tamil", "Telugu"].map(l => <SelectItem key={l} value={l} className="mono">{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><span className="font-sans">Weekly email digest</span><Switch data-testid="pref-digest" checked={!!s.email_digest} onCheckedChange={v => save({ email_digest: v })} /></div>
            <div className="mono text-white/40">Currency: {s.currency || "INR"} (₹)</div>
          </div>
        )}
        {sub === "general" && (
          <div className="space-y-4" data-testid="settings-general">
            <p className="mono text-white/50">General platform preferences.</p>
            <div className="flex items-center justify-between"><span className="font-sans">Push notifications</span><Switch data-testid="gen-notifications" checked={!!s.notifications} onCheckedChange={v => save({ notifications: v })} /></div>
            <div className="flex items-center justify-between"><span className="font-sans">Reduce motion</span><Switch data-testid="gen-reduce-motion" checked={!!s.reduce_motion} onCheckedChange={v => save({ reduce_motion: v })} /></div>
            <div className="flex items-center justify-between"><span className="font-sans">Show mature content</span><Switch data-testid="gen-mature" checked={!!s.show_mature} onCheckedChange={v => save({ show_mature: v })} /></div>
            <div><Label className="mono text-white/60">Theme</Label>
              <Select value={s.theme || "dark"} onValueChange={v => save({ theme: v })}>
                <SelectTrigger data-testid="gen-theme" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{["dark", "midnight", "cinema"].map(t => <SelectItem key={t} value={t} className="mono">{t.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        {sub === "widgets" && (          <div className="space-y-3" data-testid="settings-widgets">
            <p className="mono text-white/50">Toggle dashboard widgets.</p>
            {[["revenue", "Revenue"], ["views", "Views"], ["ratings", "Ratings"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between"><span className="font-sans">{l}</span><Switch data-testid={`widget-${k}`} checked={!!(s.widgets || {})[k]} onCheckedChange={v => setWidget(k, v)} /></div>
            ))}
          </div>
        )}
        {sub === "library" && (
          <div className="space-y-4" data-testid="settings-library">
            <div className="flex items-center justify-between"><span className="font-sans">Autoplay next</span><Switch data-testid="lib-autoplay" checked={!!s.library_autoplay} onCheckedChange={v => save({ library_autoplay: v })} /></div>
            <div><Label className="mono text-white/60">Default quality</Label>
              <Select value={s.library_default_quality || "auto"} onValueChange={v => save({ library_default_quality: v })}>
                <SelectTrigger data-testid="lib-quality" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">{["auto", "1080p", "720p", "480p"].map(q => <SelectItem key={q} value={q} className="mono">{q.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        {sub === "trash" && (
          <div data-testid="settings-trash">
            <p className="mono text-white/50 mb-3">Deleted content is kept here for recovery.</p>
            {trash.length === 0 ? <div className="mono text-white/30 text-center py-8">TRASH IS EMPTY</div> :
              trash.map(t => <div key={t.id} className="border border-white/10 rounded-sm p-3 flex items-center justify-between"><span>{t.title}</span><span className="mono text-white/40">DELETED</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ToolsStudio() {
  const [tab, setTab] = useState("creative");
  return (
    <div className="space-y-6" data-testid="tools-studio">
      <div>
        <div className="mono text-white/40">ERP & STUDIO TOOLS · THE FILMMAKER OS</div>
        <h1 className="text-4xl font-extrabold tracking-tight mt-2">Studio Tools</h1>
        <p className="text-white/60 mt-2">Generative creative tools, budgets & invoices, and pre/pro/post production.</p>
      </div>
      <div className="flex gap-1 flex-wrap">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`tools-tab-${k}`} onClick={() => setTab(k)}
            className={`mono px-4 h-9 rounded-sm border inline-flex items-center gap-2 ${tab === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "creative" && <Creative />}
      {tab === "erp" && <ERP />}
      {tab === "production" && <Production />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}
